import type { AxiosInstance } from 'axios';
import authService from './auth.service';
import type {
  ActiveCommitmentPolicy,
  AdminCancelCategory,
  AdminCancelSource,
  CancellationDecisionBody,
  CancellationDecisionResult,
  Commitment,
  CommitmentConfig,
  DepositLimit,
  DraftCommitmentBody,
  FollowupBrief,
  PublishConfigBody,
} from '../types/commitments';

export interface WithdrawalPenaltyTier {
  up_to_month: number;
  pct: string;
}

export interface WithdrawalPenalty {
  product_id: string;
  /** 'flat' | 'tiered' | null when the vehicle charges nothing. */
  method: string | null;
  has_active_config: boolean;
  version: number | null;
  flat_pct: string | null;
  tiers: WithdrawalPenaltyTier[];
  effective_from: string | null;
}


/** One row of GET /admin/commitment-configs/active: the ACTIVE config's
 *  retention per product. Prices commitments only — never client product
 *  copy (audit #138). */
export interface ActiveConfigRetention {
  product_id: string;
  early_termination_retention_pct: string;
}

class CommitmentsService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  // ── Active policy preview (any role — clients set up their own plan) ─
  /** Form-safe view of the active config: cadence/amount/lifecycle
   *  fields only — no contract body, no admin metadata. The /admin
   *  endpoint stays gated to super_admin for full version history. */
  async getActiveProductPolicy(productId: string): Promise<ActiveCommitmentPolicy> {
    const res = await this.api.get<ActiveCommitmentPolicy>(
      `/api/v1/commitments/products/${productId}/active-policy`,
    );
    return res.data;
  }

  // ── Admin: configs ──────────────────────────────────────────────
  /** Active config's retention per product (super_admin) — the status
   *  panel's own read; the product listings no longer carry it. */
  async listActiveConfigRetentions(): Promise<ActiveConfigRetention[]> {
    const res = await this.api.get<ActiveConfigRetention[]>(
      '/api/v1/admin/commitment-configs/active',
    );
    return res.data;
  }

  async listProductConfigs(productId: string): Promise<CommitmentConfig[]> {
    const res = await this.api.get<CommitmentConfig[]>(
      `/api/v1/admin/products/${productId}/commitment-configs`,
    );
    return res.data;
  }

  async publishProductConfig(
    productId: string,
    body: PublishConfigBody,
  ): Promise<{ id: string }> {
    const res = await this.api.post<{ id: string }>(
      `/api/v1/admin/products/${productId}/commitment-configs`,
      body,
    );
    return res.data;
  }

  // ── Admin/Manager: deposit limits ──────────────────────────────
  async listUserDepositLimits(userId: string): Promise<DepositLimit[]> {
    const res = await this.api.get<DepositLimit[]>(
      `/api/v1/admin/users/${userId}/deposit-limits`,
    );
    return res.data;
  }

  async setUserDepositLimit(
    userId: string,
    productId: string,
    monthlyCap: string,
    reason: string | null,
  ): Promise<DepositLimit> {
    const res = await this.api.put<DepositLimit>(
      `/api/v1/admin/users/${userId}/products/${productId}/deposit-limit`,
      { monthly_cap: monthlyCap, reason },
    );
    return res.data;
  }

  // ── Commitments: lifecycle ─────────────────────────────────────
  async draft(body: DraftCommitmentBody): Promise<{ id: string }> {
    const res = await this.api.post<{ id: string }>('/api/v1/commitments', body);
    return res.data;
  }

  /** List commitments for a client. Single URL surface; backend
   *  RBAC accepts client self, agent on roster, or manager any. */
  async listForClient(clientId: string, statuses?: string[]): Promise<Commitment[]> {
    const res = await this.api.get<Commitment[]>(
      `/api/v1/commitments/clients/${clientId}`,
      { params: statuses?.length ? { statuses } : undefined },
    );
    return res.data;
  }

  /** Shortcut: caller's own commitments (clients viewing their own
   *  list). Resolves the caller's UUID via /auth/me cache. */
  async listMine(statuses?: string[]): Promise<Commitment[]> {
    const id = await authService.getMyId();
    return this.listForClient(id, statuses);
  }

  async listPendingManager(): Promise<Commitment[]> {
    const res = await this.api.get<Commitment[]>('/api/v1/commitments/pending-manager');
    return res.data;
  }

  /**
   * The surrender charge FUNDS will actually apply, read live (0073).
   *
   * Not the same number as a config's `early_termination_retention_pct`: since
   * the vehicle's economics moved to funds, the portal's own percentage is a
   * local copy that nothing keeps in step. Showing both is the only way a
   * divergence is visible to the person configuring the product.
   */
  async withdrawalPenalty(productId: string): Promise<WithdrawalPenalty> {
    const res = await this.api.get<WithdrawalPenalty>(
      `/api/v1/admin/products/${productId}/withdrawal-penalty`,
    );
    return res.data;
  }

  async get(commitmentId: string): Promise<Commitment> {
    const res = await this.api.get<Commitment>(`/api/v1/commitments/${commitmentId}`);
    return res.data;
  }

  /** Agent-only: request cancellation of an active|paused commitment. */
  async requestCancellation(commitmentId: string, reason: string): Promise<void> {
    await this.api.post(
      `/api/v1/commitments/${commitmentId}/request-cancellation`,
      { reason },
    );
  }

  /** Manager-only: dispatch one of three decisions on a
   *  pending_cancellation commitment. The body's required fields
   *  depend on `decision_type`:
   *   · reject              → manager_notes
   *   · approve_change      → manager_notes + renegotiation_assignee_id
   *                           + account_agent_id
   *   · approve_termination → manager_notes + scheduled_return_date
   */
  async decideCancellation(
    commitmentId: string,
    body: CancellationDecisionBody,
  ): Promise<CancellationDecisionResult> {
    const res = await this.api.post<CancellationDecisionResult>(
      `/api/v1/commitments/${commitmentId}/cancellation-decision`,
      body,
    );
    return res.data;
  }

  /** Manager emergency cancel — bypasses the request/approve cycle.
   *
   *  `category` drives the monthly CNBV "cancelaciones por causa"
   *  report; the dropdown that feeds it lives in the admin-cancel
   *  modal. `external_reference` is REQUIRED on the backend for
   *  `regulatory_action` and `legal_dispute` (folio CNBV / oficio
   *  judicial); the UI surfaces a 400 if the manager forgets it.
   *  `source` defaults to `manager_manual` — automated jobs that
   *  call this from a background worker would pass `automated_job`
   *  instead so the report can separate human vs. cron-driven
   *  cancellations without correlating service-account UUIDs. */
  async adminCancel(
    commitmentId: string,
    payload: {
      category: AdminCancelCategory;
      reason: string;
      external_reference?: string | null;
      source?: AdminCancelSource;
    },
  ): Promise<void> {
    await this.api.post(`/api/v1/commitments/${commitmentId}/admin-cancel`, {
      category: payload.category,
      source: payload.source ?? 'manager_manual',
      reason: payload.reason,
      external_reference: payload.external_reference ?? null,
    });
  }

  async toggleActivePaused(
    commitmentId: string,
    pause: boolean,
    reason: string | null,
  ): Promise<void> {
    await this.api.post(`/api/v1/commitments/${commitmentId}/pause-toggle`, {
      pause,
      reason,
    });
  }

  // ── Pre-call brief (agent/manager) ─────────────────────────────
  async getFollowupBrief(caseId: string): Promise<FollowupBrief> {
    const res = await this.api.get<FollowupBrief>(
      `/api/v1/staff/cases/${caseId}/followup-brief`,
    );
    return res.data;
  }
}

export default new CommitmentsService();
